import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { 
  Clock, Users, Activity, LogIn, LogOut, 
  RefreshCw, Circle, Calendar, Timer, Download
} from "lucide-react";
import { AgentLayout } from "./AgentDashboard";
import { API } from "../../App";
import axios from "axios";
import { toast } from "sonner";

export default function AgentTeamStatus() {
  const [loading, setLoading] = useState(true);
  const [clockStatus, setClockStatus] = useState(null);
  const [teamStatus, setTeamStatus] = useState(null);
  const [timeReport, setTimeReport] = useState(null);
  const [myHistory, setMyHistory] = useState([]);
  const [clockNotes, setClockNotes] = useState("");
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    fetchData();
    // Poll every 30 seconds
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchData = async () => {
    const token = localStorage.getItem("agent_token");
    setLoading(true);
    try {
      const [statusRes, teamRes, historyRes] = await Promise.all([
        axios.get(`${API}/agent/clock/status`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/agent/team/online-status`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => ({ data: null })),
        axios.get(`${API}/agent/clock/history`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setClockStatus(statusRes.data);
      setTeamStatus(teamRes.data);
      setMyHistory(historyRes.data.sessions);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleClock = async (action) => {
    const token = localStorage.getItem("agent_token");
    try {
      const res = await axios.post(`${API}/agent/clock`, {
        action,
        notes: clockNotes || null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(action === "clock_in" ? "Clocked in!" : "Clocked out!");
      setClockNotes("");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Action failed");
    }
  };

  const handleExportData = async () => {
    const token = localStorage.getItem("agent_token");
    setExporting(true);
    try {
      const response = await axios.get(`${API}/agent/team/time-export`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `team_time_report_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Time data exported successfully");
    } catch (error) {
      toast.error("Failed to export data");
    } finally {
      setExporting(false);
    }
  };

  const formatDuration = (hours) => {
    if (!hours) return "0h 0m";
    const h = Math.floor(hours);
    const m = Math.round((hours - h) * 60);
    return `${h}h ${m}m`;
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <AgentLayout title="Team Status">
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold" style={{ fontFamily: 'Young Serif, serif' }}>
              Team Status & Time Tracking
            </h1>
            <p className="text-muted-foreground">Clock in/out and monitor team activity</p>
          </div>
          <div className="flex gap-2">
            {teamStatus && (
              <Button onClick={handleExportData} variant="outline" disabled={exporting}>
                <Download className={`h-4 w-4 mr-2 ${exporting ? 'animate-pulse' : ''}`} />
                Export CSV
              </Button>
            )}
            <Button onClick={fetchData} variant="outline" disabled={loading}>
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>

        {/* Clock In/Out Card */}
        <Card className="border-2 border-primary">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5 text-primary" />
              My Clock Status
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className={`w-16 h-16 rounded-full flex items-center justify-center ${
                  clockStatus?.is_clocked_in 
                    ? 'bg-green-100 text-green-600' 
                    : 'bg-gray-100 text-gray-400'
                }`}>
                  <Circle className={`h-8 w-8 ${clockStatus?.is_clocked_in ? 'fill-current' : ''}`} />
                </div>
                <div>
                  <p className="text-lg font-semibold">
                    {clockStatus?.is_clocked_in ? "Currently Clocked In" : "Not Clocked In"}
                  </p>
                  {clockStatus?.current_session && (
                    <p className="text-sm text-muted-foreground">
                      Since {formatTime(clockStatus.current_session.clock_in)}
                    </p>
                  )}
                </div>
              </div>

              <div className="flex items-center gap-2">
                <Input
                  placeholder="Notes (optional)"
                  value={clockNotes}
                  onChange={(e) => setClockNotes(e.target.value)}
                  className="w-48"
                />
                {clockStatus?.is_clocked_in ? (
                  <Button 
                    variant="destructive" 
                    onClick={() => handleClock("clock_out")}
                  >
                    <LogOut className="h-4 w-4 mr-2" />
                    Clock Out
                  </Button>
                ) : (
                  <Button onClick={() => handleClock("clock_in")}>
                    <LogIn className="h-4 w-4 mr-2" />
                    Clock In
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Team Online Status */}
        {teamStatus && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                Team Online Status
              </CardTitle>
              <CardDescription>
                {teamStatus.online_count} online, {teamStatus.away_count || 0} away of {teamStatus.total_count} team members
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {teamStatus.agents.map((agent) => {
                  const activityStatus = agent.activity_status || (agent.is_online ? 'online' : 'offline');
                  const statusConfig = {
                    online: { bg: 'bg-green-50 border-green-200', dot: 'bg-green-500', badge: 'default', label: 'Online' },
                    away: { bg: 'bg-yellow-50 border-yellow-200', dot: 'bg-yellow-500', badge: 'secondary', label: 'Away' },
                    offline: { bg: 'bg-gray-50 border-gray-200', dot: 'bg-gray-300', badge: 'outline', label: 'Offline' }
                  };
                  const config = statusConfig[activityStatus] || statusConfig.offline;
                  
                  return (
                    <div 
                      key={agent.agent_id} 
                      className={`p-4 rounded-lg border ${config.bg}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`w-3 h-3 rounded-full ${config.dot}`} />
                          <div>
                            <p className="font-medium">{agent.full_name}</p>
                            <p className="text-xs text-muted-foreground capitalize">{agent.role}</p>
                          </div>
                        </div>
                        <Badge variant={config.badge} className={activityStatus === 'away' ? 'bg-yellow-100 text-yellow-800' : ''}>
                          {config.label}
                        </Badge>
                      </div>
                      {(agent.last_activity || agent.last_clock_in) && (
                        <p className="text-xs text-muted-foreground mt-2">
                          Last active: {new Date(agent.last_activity || agent.last_clock_in || agent.last_clock_out).toLocaleString()}
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* My Time History */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Timer className="h-5 w-5" />
              My Time History
            </CardTitle>
          </CardHeader>
          <CardContent>
            {myHistory.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Calendar className="h-12 w-12 mx-auto mb-2 opacity-50" />
                <p>No time records yet</p>
              </div>
            ) : (
              <div className="space-y-3">
                {myHistory.map((session) => (
                  <div 
                    key={session.session_id} 
                    className="flex items-center justify-between p-3 bg-muted/50 rounded-lg"
                  >
                    <div className="flex items-center gap-4">
                      <div className="text-center">
                        <p className="text-sm font-medium">
                          {new Date(session.clock_in).toLocaleDateString([], { weekday: 'short' })}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {new Date(session.clock_in).toLocaleDateString([], { month: 'short', day: 'numeric' })}
                        </p>
                      </div>
                      <div>
                        <p className="font-medium">
                          {formatTime(session.clock_in)} - {session.clock_out ? formatTime(session.clock_out) : 'In Progress'}
                        </p>
                        {session.notes && (
                          <p className="text-xs text-muted-foreground">Note: {session.notes}</p>
                        )}
                      </div>
                    </div>
                    <div className="text-right">
                      {session.clock_out ? (
                        <Badge variant="outline">{formatDuration(session.duration_hours)}</Badge>
                      ) : (
                        <Badge className="bg-green-100 text-green-800">Active</Badge>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AgentLayout>
  );
}
